import CollectionGenerators from './CollectionGenerators';
import DefaultEqualityComparer from '../system/DefaultEqualityComparer';
import Enumerable from '../collections/Enumerable';
import GeneratorFactory from './GeneratorFactory';
import InvalidOperationError from '../system/InvalidOperationError';
import MathUtils from '../math/MathUtils';
import NumberUtils from '../primitives/NumberUtils';
export default class Collection extends Enumerable {
    constructor(values = []) {
        super(values);
        if (Array.isArray(values)) {
            this.array = values;
        }
        else {
            this.array = Array.from(values);
            this.iterable = this.array;
        }
    }
    get length() {
        return this.array.length;
    }
    atOffsetOfFirst(predicate, offset) {
        const index = this.array.findIndex(predicate);
        if (index !== -1 && this.isIndexInRange(index + offset)) {
            return this.array[index + offset];
        }
        else {
            return undefined;
        }
    }
    clear() {
        this.array = [];
        this.iterable = this.array;
    }
    clone(startIndex, endIndexExclusive) {
        return new Enumerable(new GeneratorFactory(() => CollectionGenerators.clone(this, startIndex, endIndexExclusive)));
    }
    count(predicate) {
        if (predicate === undefined) {
            return this.array.length;
        }
        return new Enumerable(this).count(predicate);
    }
    dimensionCount() {
        let dimensions = 0;
        let array = this;
        while (Array.isArray(array)) {
            dimensions += 1;
            array = array[0];
        }
        return dimensions;
    }
    firstIndex(predicate, start = 0) {
        for (let i = start; i < this.array.length; i++) {
            if (!predicate || predicate(this.array[i])) {
                return i;
            }
        }
        return -1;
    }
    flatten() {
        return this.array.reduce(function (flat, toFlatten) {
            return flat.concat(toFlatten);
        }, []);
    }
    get(index) {
        return this.array[index];
    }
    getFromEnd(reverseIndex) {
        return this.array[this.array.length - reverseIndex - 1];
    }
    getOrDefault(index, defaultValue) {
        if (this.isIndexInRange(index)) {
            return this.array[index];
        }
        else {
            return defaultValue;
        }
    }
    getWithinRange(index, startIndex = 0, endIndexExclusive) {
        endIndexExclusive = endIndexExclusive !== null && endIndexExclusive !== void 0 ? endIndexExclusive : this.array.length - 1;
        return this.array[MathUtils.wrap(index, startIndex, endIndexExclusive)];
    }
    insert(item, index = 0) {
        this.array.splice(index, 0, item);
    }
    isEmpty() {
        return this.array.length === 0;
    }
    isIndexInRange(index) {
        return index >= 0 && index <= this.array.length - 1;
    }
    last(predicate) {
        const value = this.lastOrDefault(predicate);
        if (value === undefined) {
            throw new InvalidOperationError('Sequence contains no elements.');
        }
        return value;
    }
    lastIndex(predicate, start) {
        let i = this.array.length - 1 - (start || 0);
        while (i >= 0) {
            if (predicate === undefined || predicate(this.array[i])) {
                return i;
            }
            i--;
        }
        return -1;
    }
    lastOrDefault(predicate, defaultValue) {
        const index = this.lastIndex(predicate);
        return index !== -1 ? this.array[index] : defaultValue;
    }
    maxIndex(selector) {
        let maxValue;
        let maxIndex = -1;
        for (let i = 0; i < this.array.length; i++) {
            const value = selector ? selector(this.array[i]) : this.array[i];
            if (value !== undefined && (maxValue === undefined || NumberUtils.compare(value, maxValue) === 1)) {
                maxValue = value;
                maxIndex = i;
            }
        }
        return maxIndex;
    }
    minIndex(selector) {
        let minValue;
        let minIndex = -1;
        for (let i = 0; i < this.array.length; i++) {
            const value = selector ? selector(this.array[i]) : this.array[i];
            if (value !== undefined && (minValue === undefined || NumberUtils.compare(value, minValue) === 1)) {
                minValue = value;
                minIndex = i;
            }
        }
        return minIndex;
    }
    move(sourceIndex, targetIndex) {
        this.moveRange(sourceIndex, sourceIndex + 1, targetIndex);
    }
    moveRange(startIndex, endIndexExclusive, targetIndex) {
        if (startIndex < 0 || startIndex >= this.array.length) {
            throw new RangeError('Invalid start index.');
        }
        else if (endIndexExclusive < 0 || endIndexExclusive > this.array.length) {
            throw new RangeError('Invalid end index.');
        }
        else if (targetIndex < 0 || targetIndex > this.array.length) {
            throw new RangeError('Invalid target range.');
        }
        const rangeLength = endIndexExclusive - startIndex;
        const slice = this.array.slice(startIndex, endIndexExclusive);
        if (targetIndex < startIndex) {
            for (let i = endIndexExclusive - 1; i >= targetIndex + rangeLength; i--) {
                this.array[i] = this.array[i - rangeLength];
            }
            for (let i = 0; i < slice.length; i++) {
                this.array[targetIndex + i] = slice[i];
            }
        }
        else if (targetIndex > startIndex) {
            for (let i = startIndex; i < targetIndex - rangeLength; i++) {
                this.array[i] = this.array[i + rangeLength];
            }
            for (let i = 0; i < slice.length; i++) {
                this.array[targetIndex - 1 + i] = slice[i];
            }
        }
    }
    pop() {
        return this.array.pop();
    }
    push(value) {
        this.array.push(value);
    }
    pushIfAbsent(value) {
        if (!this.exists(v => DefaultEqualityComparer.equal(v, value))) {
            this.array.push(value);
        }
    }
    pushMany(values) {
        values.forEach(v => this.array.push(v));
    }
    remove(item) {
        const index = this.firstIndex(f => DefaultEqualityComparer.equal(f, item));
        if (index !== -1) {
            this.array.splice(index, 1);
        }
    }
    removeAt(index, count = 1) {
        this.array.splice(index, count);
    }
    removeRange(startIndex, endIndexExclusive) {
        if (endIndexExclusive !== undefined) {
            this.array.splice(startIndex, endIndexExclusive - startIndex);
        }
        else {
            this.array.splice(startIndex);
        }
    }
    removeWhere(predicate) {
        let i = this.array.length;
        while (i--) {
            if (predicate(this.array[i])) {
                this.array.splice(i, 1);
            }
        }
    }
    replaceWith(newElements, keySelector, onReplace) {
        return new Enumerable(new GeneratorFactory(() => CollectionGenerators.replaceWith(this, newElements, keySelector, onReplace)));
    }
    reversed() {
        return new Enumerable(new GeneratorFactory(() => CollectionGenerators.reversed(this)));
    }
    swap(index1, index2) {
        const item1 = this.array[index1];
        this.array[index1] = this.array[index2];
        this.array[index2] = item1;
    }
    toEnumerable() {
        return new Enumerable(this);
    }
    toString() {
        return this.array.toString();
    }
}
//# sourceMappingURL=Collection.js.map